import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { LinkCell, StatusCell } from 'core/elements';
import { LinkTarget } from 'core/enums';
import { formatDate } from 'core/utils';
import { SubmitOrderOutcome } from 'dashboard/enums';
import { ISubmitOrderLogEntry } from 'dashboard/types';
import { orderPartsPath } from 'orders/routes/routePaths';
import { Color } from 'theme/enums';
import { IColorMap } from 'theme/types';

import { SubmitOrderLogColId } from './enums';
import { PayloadCell } from './PayloadCell';
import { getSubmitOrderOutcomeLabels } from '../utils';

const BYTES_PER_KB = 1024;

const outcomeColorMap: IColorMap = {
  [SubmitOrderOutcome.FAILED]: Color.DANGER,
  [SubmitOrderOutcome.NO_RESPONSE]: Color.WARNING,
  [SubmitOrderOutcome.SUCCESS]: Color.SUCCESS,
};

// null/undefined and empty strings render as the placeholder; a legitimate 0
// (e.g. a connection-level HTTP status) must stay visible.
const formatWithPlaceholder = ({ value }: ValueFormatterParams) =>
  value === '' || value == null ? DEFAULT_PLACEHOLDER : value;

// Floating filters are opt-out rather than opt-in: useAgGridLayout rewrites
// floatingFilter on every client-table column from the row count, and only an explicit
// `false` survives that. So the two columns kept out of the filter row say so here,
// and the rest (company, user, order #, result, HTTP status, external order #, error,
// error description, duration, size) get the row once the result set passes the
// shared threshold.
export const columnDef = (): ColDef[] => {
  const outcomeLabels = getSubmitOrderOutcomeLabels();

  return [
    {
      colId: SubmitOrderLogColId.CREATED_AT,
      headerName: t('headerDateTime'),
      field: 'createdAt',
      filter: 'agDateColumnFilter',
      floatingFilter: false,
      enableRowGroup: false,
      minWidth: 170,
      // formatDate returns the placeholder for falsy input itself.
      valueFormatter: ({ value }) => formatDate(value, true),
    },
    {
      colId: SubmitOrderLogColId.COMPANY,
      headerName: t('headerCompany'),
      field: 'companyName',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      minWidth: 150,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.USER,
      headerName: t('headerUser'),
      field: 'userName',
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      minWidth: 150,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.ORDER_ID,
      headerName: t('headerOrderId'),
      field: 'orderId',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      width: 110,
      cellRenderer: LinkCell,
      cellRendererParams: {
        // A new tab: the log is a working view (loaded range, filters, group-by)
        // that an admin investigating a submission should not lose to a navigation.
        target: LinkTarget.BLANK,
        getPath: ({ orderId }: ISubmitOrderLogEntry) => (orderId ? orderPartsPath(orderId) : null),
      },
      // Display comes from LinkCell (which renders the placeholder itself); this
      // is what the Excel export writes.
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.OUTCOME,
      headerName: t('headerResult'),
      field: 'outcome',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => outcomeLabels[value],
      },
      enableRowGroup: true,
      width: 140,
      cellRenderer: StatusCell,
      cellRendererParams: { colorMap: outcomeColorMap, labelMap: outcomeLabels },
      // The failure detail rides in the tooltip so the grid stays scannable. Falls
      // back to the description because a non-JSON failure (an HTML error page) lands
      // its whole text in errorDescription and leaves errorMessage null (back-end #4400).
      tooltipValueGetter: ({ data }) => data?.errorMessage ?? data?.errorDescription,
    },
    {
      colId: SubmitOrderLogColId.HTTP_STATUS,
      headerName: t('headerHttpStatus'),
      field: 'responseStatus',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      width: 120,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.EXTERNAL_ORDER_NUMBER,
      headerName: t('headerExternalOrderNumber'),
      field: 'externalOrderNumber',
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      minWidth: 150,
      cellRenderer: LinkCell,
      cellRendererParams: {
        // The vendor system's own deep link (the URL the order details screen
        // uses), so a raw href rather than a router path.
        external: true,
        target: LinkTarget.BLANK,
        getPath: ({ externalOrderUrl }: ISubmitOrderLogEntry) => externalOrderUrl,
      },
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.ERROR,
      headerName: t('headerError'),
      field: 'errorMessage',
      // The high-level reason only ("Bad Request", "Order already exists"), so it
      // stays short enough to scan and repeats across rows — which is what makes
      // the filter row worth having on it. The per-field detail is the next column.
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      minWidth: 180,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.ERROR_DESCRIPTION,
      headerName: t('headerErrorDescription'),
      field: 'errorDescription',
      filter: 'agTextColumnFilter',
      enableRowGroup: false,
      minWidth: 320,
      // Runs to several hundred characters (one clause per rejected field), so the
      // cell can only ever show the start of it — the full text is on hover, and
      // the whole response is still a click away in the payload panel.
      tooltipValueGetter: ({ value }) => value,
      valueFormatter: formatWithPlaceholder,
    },
    {
      colId: SubmitOrderLogColId.DURATION,
      headerName: t('headerDuration'),
      field: 'durationMs',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      width: 110,
      valueFormatter: ({ value }) =>
        value == null ? DEFAULT_PLACEHOLDER : t('labelDurationMs', { milliseconds: value }),
    },
    {
      colId: SubmitOrderLogColId.SIZE,
      headerName: t('headerSizeKb'),
      field: 'requestBodyBytes',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      enableRowGroup: false,
      width: 110,
      // Kept numeric (not a "48.2 KB" string) so sorting and the number filter work.
      valueGetter: ({ data }) =>
        data && Math.round((data.requestBodyBytes / BYTES_PER_KB) * 10) / 10,
    },
    {
      colId: SubmitOrderLogColId.PAYLOAD,
      headerName: t('headerPayload'),
      sortable: false,
      filter: false,
      floatingFilter: false,
      enableRowGroup: false,
      width: 120,
      cellRenderer: PayloadCell,
      // Nothing meaningful to export or copy from the cell itself.
      valueFormatter: () => '',
    },
  ];
};
